import { Fragment } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';

import { CodeSpan } from '../../CodeSpan';
import { getAnalysisTypeDisplayName } from '../../getAnalysisTypeDisplayName';
import { getSuccessCriteriaDisplayName } from '../../getSuccessCriteriaDisplayName';
import { RandomizationUnitsSection } from '../../RandomizationUnitsSection';
import type { MeasureType, Metric } from '../../types';

import styles from './MetricDefinition.module.css';

interface MetricDefinitionProps {
  metric: Metric;
  className?: string;
  /**
   * Optional list of randomization units that are actually in use.
   * When provided, only these randomization units will be shown,
   * rather than all randomization units the metric supports.
   */
  randomizationUnitsInUse?: string[];
}

type AggregationSentencePrefix = 'Count' | 'Percent' | 'Sum' | 'Average' | 'Count distinct';

function getAggregationSentencePrefix(metric: Metric): AggregationSentencePrefix | null {
  if (metric.kind !== 'custom') {
    return null;
  }
  if (metric.unitAggregationType === 'count_distinct') {
    return 'Count distinct';
  }
  if (metric.isNumeric) {
    if (metric.unitAggregationType === 'sum') {
      return 'Sum';
    }
    if (metric.unitAggregationType === 'average') {
      return 'Average';
    }
    return null;
  }
  if (metric.unitAggregationType === 'sum') {
    return 'Count';
  }
  if (metric.unitAggregationType === 'average') {
    return 'Percent';
  }
  return null;
}

function sentencePrefixLabel(prefix: AggregationSentencePrefix): string {
  switch (prefix) {
    case 'Count':
      return msg('metrics.definitionSummary.countPrefix');
    case 'Sum':
      return msg('metrics.definitionSummary.sumPrefix');
    case 'Average':
      return msg('metrics.definitionSummary.averagePrefix');
    case 'Percent':
      return msg('metrics.definitionSummary.percentPrefix');
    case 'Count distinct':
      return msg('metrics.definitionSummary.countDistinctPrefix');
    default:
      return prefix;
  }
}

/**
 * Returns true when MetricDefinition renders the aggregation-driven sentence
 * that includes the success criteria inline. Callers (e.g. MetricConfiguration)
 * can use this to avoid showing a duplicate "Success criteria" row.
 */
export function metricDefinitionIncludesSuccessCriteria(metric: Metric): boolean {
  return getAggregationSentencePrefix(metric) !== null;
}

const RandomizationUnitsList: React.FC<{ units: string[] }> = ({ units }) => {
  if (units.length === 0) {
    return null;
  }
  return (
    <>
      {units.map((unit, index) => (
        <Fragment key={unit}>
          {index > 0 &&
            (index === units.length - 1
              ? msg('metrics.definitionSummary.unitsLastJoiner')
              : msg('metrics.definitionSummary.unitsJoiner'))}
          <CodeSpan textValue={unit} />
        </Fragment>
      ))}
    </>
  );
};

export const MetricDefinition: React.FC<MetricDefinitionProps> = ({ metric, className, randomizationUnitsInUse }) => {
  const measureType: MeasureType = metric.isNumeric
    ? 'value'
    : metric.unitAggregationType === 'sum'
      ? 'count'
      : 'occurrence';
  const analysisTypeString = getAnalysisTypeDisplayName(
    metric.analysisType,
    metric.percentileValue,
    metric.unitAggregationType,
  );

  // Filter randomization units to only show those in use, if specified
  const metricRandomizationUnits = metric.analysisUnits ?? [];
  const filteredRandomizationUnits = randomizationUnitsInUse
    ? metricRandomizationUnits.filter((unit) => randomizationUnitsInUse.includes(unit))
    : metricRandomizationUnits;
  const randomizationUnitsToShow =
    filteredRandomizationUnits.length > 0 ? filteredRandomizationUnits : metricRandomizationUnits;

  const aggregationSentencePrefix = getAggregationSentencePrefix(metric);

  if (aggregationSentencePrefix) {
    // Legacy metrics may have an unset successCriteria; fall back to the system
    // default ('HigherThanBaseline') so the sentence stays readable and matches
    // what the picker would have shown.
    const successCriteriaText = getSuccessCriteriaDisplayName(metric.successCriteria ?? 'HigherThanBaseline');

    const hasUnits = randomizationUnitsToShow.length > 0;

    if (aggregationSentencePrefix === 'Percent') {
      return (
        <span className={className} id="metricDefinition">
          <CodeSpan textValue={msg('metrics.definitionSummary.percentPrefix')} className={styles.codeSpanWithMargin} />{' '}
          {hasUnits ? (
            <Message
              id="metrics.definitionSummary.percentSentence"
              values={{
                units: () => <RandomizationUnitsList units={randomizationUnitsToShow} />,
                criteria: successCriteriaText,
              }}
            />
          ) : (
            <Message id="metrics.definitionSummary.percentSentenceNoUnits" values={{ criteria: successCriteriaText }} />
          )}
        </span>
      );
    }

    if (aggregationSentencePrefix === 'Count distinct') {
      const column = metric.unitAggregationField;
      // Metrics stored without an analysis type are analyzed as a mean, which is
      // also what the edit form falls back to.
      const countAnalysisTypeString = getAnalysisTypeDisplayName(
        metric.analysisType ?? 'mean',
        metric.percentileValue,
        metric.unitAggregationType,
      );
      return (
        <span className={className} id="metricDefinition">
          <CodeSpan
            textValue={msg('metrics.definitionSummary.countDistinctPrefix')}
            className={styles.codeSpanWithMargin}
          />
          <Message
            id="metrics.definitionSummary.countDistinctSentence"
            values={{
              hasColumn: column ? 'true' : 'false',
              hasUnits: hasUnits ? 'true' : 'false',
              column: () => (column ? <CodeSpan textValue={column} /> : null),
              units: () => <RandomizationUnitsList units={randomizationUnitsToShow} />,
              analysisType: () => <CodeSpan textValue={countAnalysisTypeString} />,
              criteria: successCriteriaText,
            }}
          />
        </span>
      );
    }

    return (
      <span className={className} id="metricDefinition">
        <CodeSpan textValue={sentencePrefixLabel(aggregationSentencePrefix)} className={styles.codeSpanWithMargin} />{' '}
        <Message
          id="metrics.definitionSummary.valueSentence"
          values={{
            noun: aggregationSentencePrefix === 'Count' ? 'count' : 'value',
            hasUnits: hasUnits ? 'true' : 'false',
            units: () => <RandomizationUnitsList units={randomizationUnitsToShow} />,
            analysisType: () => <CodeSpan textValue={analysisTypeString} />,
            criteria: successCriteriaText,
          }}
        />
      </span>
    );
  }

  return (
    <span className={className} id="metricDefinition">
      {measureType !== 'occurrence' && (
        <>
          <CodeSpan textValue={analysisTypeString} className={styles.codeSpanWithMargin} />{' '}
        </>
      )}
      <RandomizationUnitsSection
        measureType={measureType}
        unitAggregationType={metric.unitAggregationType}
        metricKind={metric.kind}
        readonly
        readonlyValues={randomizationUnitsToShow}
        omitWhereConnector
      />
    </span>
  );
};
